<script setup lang="ts">
  import dayjs from "dayjs";
  import { computed, nextTick, ref } from "vue";
  import { useRouter } from "vue-router";
  import { useComparisonStore } from "@/entities/comparison";
  import type { ComparisonMethod, ComparisonMode, ComparisonTarget } from "@/entities/comparison";
  import type { IndicatorType, PeriodType } from "@/entities/report";
  import { PAGES } from "@/entities/tech";
  // Reused report-wizard steps (period + indicators), parameterised with
  // comparison wording.
  import Step3Period, {
    type SelectedPeriod,
  } from "@/components/molecules/constructor/Step3Period.vue";
  import Step5Indicators, {
    BAYESIAN_DEFAULT_INDICATORS,
  } from "@/components/molecules/constructor/Step5Indicators.vue";
  import { usePersistedSet } from "@/hooks/use-persisted-set";
  import Step1TargetMode from "./components/Step1TargetMode.vue";
  import Step2Target from "./components/Step2Target.vue";
  import Step4Method from "./components/Step4Method.vue";

  const router = useRouter();
  const comparisonStore = useComparisonStore();

  type StepInstance = { rootEl: HTMLElement | null };

  const currentStep = ref(1);
  const step1Ref = ref<StepInstance | null>(null);
  const step2Ref = ref<StepInstance | null>(null);
  const step3Ref = ref<StepInstance | null>(null);
  const step4Ref = ref<StepInstance | null>(null);
  const step5Ref = ref<StepInstance | null>(null);

  const target = ref<ComparisonTarget>("trafficProvider");
  const mode = ref<ComparisonMode>("previous_period");
  const valueOne = ref("");
  const valueTwo = ref("");
  const periodKey = ref<SelectedPeriod>(11);
  const dateFrom = ref<string | null>(null);
  const dateTo = ref<string | null>(null);
  const method = ref<ComparisonMethod>("factor");
  // Persisted separately from the report wizard so each remembers its own last
  // indicator selection across visits.
  const indicators = usePersistedSet("create-comparison:step5-indicators");

  // Step 4 (method) only exists in "anotherTarget" mode; "previous_period" goes
  // straight from period (3) to indicators (5).
  const hasMethodStep = computed(() => mode.value === "anotherTarget");

  function scrollToStep(step: number) {
    const refs = [step1Ref, step2Ref, step3Ref, step4Ref, step5Ref];
    refs[step - 1]?.value?.rootEl?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function reveal(step: number) {
    if (step > currentStep.value) currentStep.value = step;
    await nextTick();
    scrollToStep(step);
  }

  // After the period step, branch around the optional method step.
  function afterPeriod() {
    void reveal(hasMethodStep.value ? 4 : 5);
  }
  function backFromIndicators() {
    scrollToStep(hasMethodStep.value ? 4 : 3);
  }

  async function submit() {
    // "previous_period" always uses the factor analysis; in "anotherTarget" the
    // chosen method decides — Bayesian opens its own (mock) page with a fixed
    // default indicator set (no indicators step), everything else the factor
    // analysis.
    const isBayesian = mode.value === "anotherTarget" && method.value === "bayesian";
    const id = await comparisonStore.add({
      target: target.value,
      mode: mode.value,
      targetValueOne: valueOne.value,
      targetValueTwo: mode.value === "anotherTarget" ? valueTwo.value : "",
      period: typeof periodKey.value === "number" ? (periodKey.value as PeriodType) : 0,
      method: mode.value === "anotherTarget" ? method.value : "factor",
      indicators: (isBayesian
        ? BAYESIAN_DEFAULT_INDICATORS
        : Array.from(indicators.value)) as IndicatorType[],
      createdDate: dayjs().format("DD.MM.YYYY HH:mm"),
      tableData: { filters: {}, rows: [] },
    });

    void router.push({
      name: isBayesian ? PAGES.BayesianCompare : PAGES.FactorComparison,
      params: { id: String(id) },
    });
  }
</script>

<template>
  <div :class="$style.base">
    <Step1TargetMode ref="step1Ref" v-model:target="target" v-model:mode="mode" @next="reveal(2)" />
    <Step2Target
      v-if="currentStep >= 2"
      ref="step2Ref"
      v-model:value-one="valueOne"
      v-model:value-two="valueTwo"
      :target="target"
      :mode="mode"
      @next="reveal(3)"
      @back="scrollToStep(1)"
    />
    <Step3Period
      v-if="currentStep >= 3"
      ref="step3Ref"
      v-model="periodKey"
      v-model:date-from="dateFrom"
      v-model:date-to="dateTo"
      :subtitle="
        $t(
          'page.createComparison.step3.subtitle',
          {},
          { default: 'Период, по которому сравниваем кампании' },
        )
      "
      @next="afterPeriod"
      @back="scrollToStep(2)"
    />
    <Step4Method
      v-if="hasMethodStep && currentStep >= 4"
      ref="step4Ref"
      v-model="method"
      @next="reveal(5)"
      @back="scrollToStep(3)"
      @submit="submit"
    />
    <Step5Indicators
      v-if="currentStep >= 5"
      ref="step5Ref"
      v-model="indicators"
      :title="$t('page.createComparison.step5.title', {}, { default: 'Показатели' })"
      :subtitle="
        $t(
          'page.createComparison.step5.subtitle',
          {},
          {
            default:
              'Выберите карточки KPI для сводки — состав столбцов таблицы настраивается отдельно',
          },
        )
      "
      :submit-label="$t('page.createComparison.create', {}, { default: 'Создать сравнение' })"
      @submit="submit"
      @back="backFromIndicators"
    />
  </div>
</template>

<style lang="scss" module>
  .base {
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding: 0 20px;

    overflow-y: auto;
    scroll-snap-type: y mandatory;

    display: flex;
    flex-direction: column;
  }
</style>
